import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import type { Aluno, CreateAlunoDto, UpdateAlunoDto } from '../services/alunosService';
import type { Contrato } from '../services/contratosService';
import { turmasService } from '../services/turmasService';
import { contratosService } from '../services/contratosService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &.error {
    border-color: #ef4444;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  color: #1f2937;
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ErrorMessage = styled.span`
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.25rem;
  display: block;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => {
    if (props.variant === 'primary') {
      return `
        background-color: #3b82f6;
        border-color: #3b82f6;
        color: white;

        &:hover:not(:disabled) {
          background-color: #2563eb;
          border-color: #2563eb;
        }

        &:disabled {
          background-color: #9ca3af;
          border-color: #9ca3af;
          cursor: not-allowed;
        }
      `;
    } else {
      return `
        background-color: white;
        border-color: #d1d5db;
        color: #374151;

        &:hover {
          background-color: #f9fafb;
          border-color: #9ca3af;
        }
      `;
    }
  }}

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ResponsaveisSection = styled.div`
  margin-top: 0.5rem;
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
`;

const ResponsaveisTitulo = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ResponsaveisSubtitulo = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 0.75rem;
  color: #64748b;
`;

const ResponsavelCard = styled.div`
  padding: 0.75rem;
  background: white;
  border-radius: 0.375rem;
  border: 1px solid #e2e8f0;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ResponsavelNome = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RoleBadge = styled.span<{ tipo: 'financeiro' | 'solidario' }>`
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  ${props => props.tipo === 'financeiro'
    ? `background-color: #dbeafe; color: #1e40af;`
    : `background-color: #fef3c7; color: #92400e;`}
`;

const ResponsavelDetalhe = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.5;
`;

const SemResponsavel = styled.div`
  font-size: 0.8rem;
  color: #94a3b8;
  font-style: italic;
`;

interface AlunoFormModalProps {
  isOpen: boolean;
  aluno?: Aluno | null;
  contrato?: Contrato | null;
  onClose: () => void;
  onSubmit: (data: CreateAlunoDto | UpdateAlunoDto) => Promise<void>;
}

interface FormData {
  nome: string;
  matricula: string;
  dataNascimento: string;
  cpf: string;
  rg: string;
  email: string;
  telefone: string;
  endereco: string;
  turmaId: string;
}

interface FormErrors {
  nome?: string;
  matricula?: string;
  dataNascimento?: string;
  turmaId?: string;
}

export const AlunoFormModal: React.FC<AlunoFormModalProps> = ({
  isOpen,
  aluno,
  contrato,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    matricula: '',
    dataNascimento: '',
    cpf: '',
    rg: '',
    email: '',
    telefone: '',
    endereco: '',
    turmaId: '',
  });

  const [turmas, setTurmas] = useState<any[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emitirNfse, setEmitirNfse] = useState<boolean>(true);

  const isEditing = !!aluno;

  useEffect(() => {
    const loadTurmas = async () => {
      try {
        const data = await turmasService.findAll();
        setTurmas(data);
      } catch (error) {
        console.error('Erro ao carregar turmas:', error);
      }
    };

    loadTurmas();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (aluno) {
        setFormData({
          nome: aluno.nome,
          matricula: aluno.matricula,
          dataNascimento: aluno.dataNascimento,
          cpf: aluno.cpf || '',
          rg: aluno.rg || '',
          email: aluno.email || '',
          telefone: aluno.telefone || '',
          endereco: aluno.endereco || '',
          turmaId: aluno.turmaId?.toString() || '',
        });
        if (contrato) {
          setEmitirNfse(contrato.emitirNfse !== false);
        }
      } else {
        setFormData({
          nome: '',
          matricula: '',
          dataNascimento: '',
          cpf: '',
          rg: '',
          email: '',
          telefone: '',
          endereco: '',
          turmaId: '',
        });
      }
      setErrors({});
    }
  }, [isOpen, aluno]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.matricula.trim()) {
      newErrors.matricula = 'Matrícula é obrigatória';
    }

    if (!formData.dataNascimento) {
      newErrors.dataNascimento = 'Data de nascimento é obrigatória';
    }

    if (!formData.turmaId) {
      newErrors.turmaId = 'Turma é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        nome: formData.nome.trim(),
        matricula: formData.matricula.trim(),
        dataNascimento: formData.dataNascimento,
        cpf: formData.cpf.trim() || undefined,
        rg: formData.rg.trim() || undefined,
        email: formData.email.trim() || undefined,
        telefone: formData.telefone.trim() || undefined,
        endereco: formData.endereco.trim() || undefined,
        turmaId: parseInt(formData.turmaId),
      };

      await onSubmit(submitData);

      if (isEditing && contrato && emitirNfse !== contrato.emitirNfse) {
        await contratosService.updateEmissao(contrato.id, { emitirNfse });
      }

      onClose();
    } catch (error) {
      console.error('Erro ao salvar aluno:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose} onKeyDown={handleKeyDown}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {isEditing ? '✏️ Editar Aluno' : '➕ Novo Aluno'}
          </ModalTitle>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Nome completo do aluno"
                className={errors.nome ? 'error' : ''}
                maxLength={100}
              />
              {errors.nome && <ErrorMessage>{errors.nome}</ErrorMessage>}
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label htmlFor="matricula">Matrícula *</Label>
                <Input
                  id="matricula"
                  type="text"
                  value={formData.matricula}
                  onChange={(e) => setFormData({ ...formData, matricula: e.target.value })}
                  placeholder="Ex: ALU-2024-001"
                  className={errors.matricula ? 'error' : ''}
                  maxLength={11}
                  disabled={isEditing}
                />
                {errors.matricula && <ErrorMessage>{errors.matricula}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="dataNascimento">Data de Nascimento *</Label>
                <Input
                  id="dataNascimento"
                  type="date"
                  value={formData.dataNascimento}
                  onChange={(e) => setFormData({ ...formData, dataNascimento: e.target.value })}
                  className={errors.dataNascimento ? 'error' : ''}
                />
                {errors.dataNascimento && <ErrorMessage>{errors.dataNascimento}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  type="text"
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="rg">RG</Label>
                <Input
                  id="rg"
                  type="text"
                  value={formData.rg}
                  onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                  placeholder="00.000.000-0"
                  maxLength={20}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="aluno@example.com"
                maxLength={100}
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="turmaId">Turma *</Label>
                <Select
                  id="turmaId"
                  value={formData.turmaId}
                  onChange={(e) => setFormData({ ...formData, turmaId: e.target.value })}
                  className={errors.turmaId ? 'error' : ''}
                >
                  <option value="">Selecione uma turma</option>
                  {turmas.map((turma) => (
                    <option key={turma.id} value={turma.id}>
                      {turma.nome}
                    </option>
                  ))}
                </Select>
                {errors.turmaId && <ErrorMessage>{errors.turmaId}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label htmlFor="endereco">Endereço</Label>
              <Input
                id="endereco"
                type="text"
                value={formData.endereco}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                placeholder="Rua, número, complemento"
                maxLength={200}
              />
            </FormGroup>

            {isEditing && (
              <ResponsaveisSection>
                <ResponsaveisTitulo>
                  Responsáveis do contrato
                </ResponsaveisTitulo>
                <ResponsaveisSubtitulo>
                  Financeiro = responsável principal (main). Solidário = responsável alternativo.
                </ResponsaveisSubtitulo>
                {contrato ? (
                  <>
                    {contrato.responsavelPrincipal ? (
                      <ResponsavelCard>
                        <ResponsavelNome>
                          {contrato.responsavelPrincipal.nome}
                          <RoleBadge tipo="financeiro">Financeiro (main)</RoleBadge>
                        </ResponsavelNome>
                        <ResponsavelDetalhe>
                          {contrato.responsavelPrincipal.cpfCnpj && <>CPF: {contrato.responsavelPrincipal.cpfCnpj}<br /></>}
                          {contrato.responsavelPrincipal.telefone && <>Tel: {contrato.responsavelPrincipal.telefone}</>}
                        </ResponsavelDetalhe>
                      </ResponsavelCard>
                    ) : null}
                    {contrato.responsavelAlternativo ? (
                      <ResponsavelCard>
                        <ResponsavelNome>
                          {contrato.responsavelAlternativo.nome}
                          <RoleBadge tipo="solidario">Solidário (alternativo)</RoleBadge>
                        </ResponsavelNome>
                        <ResponsavelDetalhe>
                          {contrato.responsavelAlternativo.cpfCnpj && <>CPF: {contrato.responsavelAlternativo.cpfCnpj}<br /></>}
                          {contrato.responsavelAlternativo.telefone && <>Tel: {contrato.responsavelAlternativo.telefone}</>}
                        </ResponsavelDetalhe>
                      </ResponsavelCard>
                    ) : null}
                    {!contrato.responsavelPrincipal && !contrato.responsavelAlternativo && (
                      <SemResponsavel>Nenhum responsável vinculado ao contrato</SemResponsavel>
                    )}

                    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                      <ResponsaveisTitulo>Configurações Fiscais</ResponsaveisTitulo>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', color: '#1f2937' }}>
                        <input
                          type="checkbox"
                          checked={emitirNfse}
                          onChange={(e) => setEmitirNfse(e.target.checked)}
                        />
                        <span>Emitir Nota Fiscal Eletrônica (NFSe) automaticamente para parcelas deste contrato</span>
                      </label>
                    </div>
                  </>
                ) : aluno?.responsaveis && aluno.responsaveis.length > 0 ? (
                  aluno.responsaveis.map((resp) => (
                    <ResponsavelCard key={resp.id}>
                      <ResponsavelNome>{resp.nome}</ResponsavelNome>
                      <ResponsavelDetalhe>
                        {resp.cpfCnpj && <>CPF: {resp.cpfCnpj}<br /></>}
                        {resp.email && <>Email: {resp.email}<br /></>}
                        {resp.telefone && <>Tel: {resp.telefone}</>}
                      </ResponsavelDetalhe>
                    </ResponsavelCard>
                  ))
                ) : (
                  <SemResponsavel>Nenhum responsável vinculado</SemResponsavel>
                )}
              </ResponsaveisSection>
            )}
          </ModalBody>

          <ModalFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>

            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting && <LoadingSpinner />}
              {isSubmitting ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Aluno'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};
